import { useState, useEffect, useCallback, useRef } from 'react';
import { MessageSquare, Send, Loader2, RefreshCw, ArrowLeft, Bot, User, Settings, Save, Phone } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function WhatsAppManager() {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [customInstructions, setCustomInstructions] = useState('');
  const [savingPrompt, setSavingPrompt] = useState(false);
  const bottomRef = useRef(null);

  const loadConvos = useCallback(() => {
    fetch(`${API}/api/whatsapp/conversations`)
      .then(r => r.json())
      .then(d => setConversations(Array.isArray(d) ? d : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadConvos(); }, [loadConvos]);
  useEffect(() => { const i = setInterval(loadConvos, 15000); return () => clearInterval(i); }, [loadConvos]);

  const openConvo = async (contact) => {
    setSelected(contact);
    setMsgLoading(true);
    try {
      const r = await fetch(`${API}/api/whatsapp/conversation/${encodeURIComponent(contact)}`);
      const d = await r.json();
      setMessages(d.messages || []);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch { setMessages([]); }
    setMsgLoading(false);
  };

  // Auto-refresh selected conversation
  useEffect(() => {
    if (!selected) return;
    const i = setInterval(() => {
      fetch(`${API}/api/whatsapp/conversation/${encodeURIComponent(selected)}`)
        .then(r => r.json())
        .then(d => {
          setMessages(d.messages || []);
        })
        .catch(() => {});
    }, 10000);
    return () => clearInterval(i);
  }, [selected]);

  const sendReply = async () => {
    if (!reply.trim() || !selected) return;
    setSending(true);
    try {
      const r = await fetch(`${API}/api/whatsapp/reply`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: selected, body: reply.trim() }),
      });
      if (r.ok) {
        setReply('');
        openConvo(selected);
        toast.success('Message envoye');
      } else {
        const d = await r.json();
        toast.error(d.detail || 'Erreur envoi');
      }
    } catch { toast.error('Erreur connexion'); }
    setSending(false);
  };

  const savePrompt = async () => {
    setSavingPrompt(true);
    try {
      await fetch(`${API}/api/whatsapp/config/prompt`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ custom_instructions: customInstructions }),
      });
      toast.success('Instructions IA mises a jour');
    } catch { toast.error('Erreur'); }
    setSavingPrompt(false);
  };

  useEffect(() => {
    fetch(`${API}/api/whatsapp/config`)
      .then(r => r.json())
      .then(d => setCustomInstructions(d.custom_instructions || ''))
      .catch(() => {});
  }, []);

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const formatPhone = (num) => (num || '').replace('whatsapp:', '');

  // Settings view
  if (showSettings) {
    return (
      <div className="space-y-4" data-testid="wa-settings">
        <button onClick={() => setShowSettings(false)} className="flex items-center gap-1 text-xs text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]">
          <ArrowLeft size={14} /> Retour
        </button>
        <div className="flex items-center gap-2 mb-2">
          <Settings size={16} className="text-green-500" />
          <h2 className="text-sm font-bold text-[rgb(var(--lm-text))]">Reglages IA WhatsApp</h2>
        </div>
        <div className="bg-[rgb(var(--lm-card))] rounded-xl border border-[rgb(var(--lm-border))]/30 p-4 space-y-3">
          <div>
            <label className="text-[10px] text-[rgb(var(--lm-text-muted))] uppercase tracking-wider block mb-1">Instructions personnalisees pour l'IA</label>
            <p className="text-[9px] text-[rgb(var(--lm-text-muted))] mb-2">L'IA utilise ces instructions en plus du contexte restaurant. Exemples : "Ne propose jamais de reduction", "Reponds toujours en catalan aussi", "Le mercredi c'est soiree paella"...</p>
            <textarea value={customInstructions} onChange={e => setCustomInstructions(e.target.value)}
              rows={8} placeholder="Ecrivez vos instructions ici..."
              className="w-full text-xs bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] rounded-lg px-3 py-2 text-[rgb(var(--lm-text))] placeholder:text-[rgb(var(--lm-text-muted))] focus:outline-none resize-none"
              data-testid="wa-custom-instructions" />
          </div>
          <Button onClick={savePrompt} disabled={savingPrompt} size="sm" className="bg-green-500 text-white text-xs h-8" data-testid="wa-save-prompt">
            {savingPrompt ? <Loader2 size={12} className="animate-spin mr-1" /> : <Save size={12} className="mr-1" />}
            Sauvegarder
          </Button>
        </div>
        <div className="bg-green-500/5 border border-green-500/15 rounded-xl p-3">
          <p className="text-[10px] text-green-600 font-bold mb-1">Comment ca marche</p>
          <div className="text-[9px] text-[rgb(var(--lm-text-muted))] space-y-0.5">
            <p>L'IA repond automatiquement a chaque message WhatsApp en se basant sur :</p>
            <p>1. Les infos du restaurant (horaires, adresse, menu du jour)</p>
            <p>2. La base de connaissances (Messagerie → Entrainer IA)</p>
            <p>3. L'historique de la conversation avec le client</p>
            <p>4. Vos instructions personnalisees ci-dessus</p>
            <p className="font-bold mt-1">Vous recevez aussi une alerte Telegram a chaque nouveau message.</p>
          </div>
        </div>
      </div>
    );
  }

  // Conversation thread view
  if (selected) {
    const convo = conversations.find(c => c.contact === selected);
    return (
      <div className="flex flex-col h-[75vh]" data-testid="wa-thread">
        {/* Header */}
        <div className="flex items-center gap-3 pb-3 border-b border-[rgb(var(--lm-border))]/20 mb-2">
          <button onClick={() => { setSelected(null); loadConvos(); }} className="p-1.5 rounded-lg hover:bg-[rgb(var(--lm-surface))] text-[rgb(var(--lm-text-muted))]">
            <ArrowLeft size={16} />
          </button>
          <div className="w-9 h-9 rounded-full bg-green-500/10 flex items-center justify-center">
            <Phone size={16} className="text-green-500" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold text-[rgb(var(--lm-text))]">{convo?.profile_name || formatPhone(selected)}</p>
            <p className="text-[9px] text-[rgb(var(--lm-text-muted))]">{formatPhone(selected)}</p>
          </div>
          <button onClick={() => openConvo(selected)} className="p-1.5 rounded-lg hover:bg-[rgb(var(--lm-surface))] text-[rgb(var(--lm-text-muted))]">
            <RefreshCw size={14} />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto space-y-2 px-1">
          {msgLoading ? (
            <div className="flex justify-center py-8"><Loader2 size={16} className="animate-spin text-[rgb(var(--lm-text-muted))]" /></div>
          ) : messages.map((m, i) => {
            const isIn = m.direction === 'in';
            return (
              <div key={i} className={`flex ${isIn ? 'justify-start' : 'justify-end'}`} data-testid={`wa-msg-${i}`}>
                <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 ${
                  isIn
                    ? 'bg-[rgb(var(--lm-card))] border border-[rgb(var(--lm-border))]/30 rounded-bl-md'
                    : m.ai_generated
                      ? 'bg-green-500/10 border border-green-500/20 rounded-br-md'
                      : 'bg-[rgb(var(--lm-gold))]/10 border border-[rgb(var(--lm-gold))]/20 rounded-br-md'
                }`}>
                  {!isIn && (
                    <div className="flex items-center gap-1 mb-0.5">
                      {m.ai_generated ? (
                        <Bot size={10} className="text-green-500" />
                      ) : (
                        <User size={10} className="text-[rgb(var(--lm-gold))]" />
                      )}
                      <span className="text-[8px] text-[rgb(var(--lm-text-muted))]">
                        {m.ai_generated ? 'IA' : 'Manuel'}
                      </span>
                    </div>
                  )}
                  <p className="text-[12px] leading-relaxed text-[rgb(var(--lm-text))] whitespace-pre-wrap">{m.body}</p>
                  <p className="text-[8px] text-[rgb(var(--lm-text-muted))] mt-1 text-right">{formatTime(m.created_at)}</p>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        {/* Reply input */}
        <div className="flex gap-2 pt-3 mt-2 border-t border-[rgb(var(--lm-border))]/20">
          <Input value={reply} onChange={e => setReply(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendReply()}
            placeholder="Repondre manuellement..."
            className="flex-1 text-xs bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] h-9"
            disabled={sending} data-testid="wa-reply-input" />
          <Button onClick={sendReply} disabled={sending || !reply.trim()} size="sm"
            className="bg-green-500 hover:bg-green-600 text-white h-9 px-3" data-testid="wa-reply-send">
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          </Button>
        </div>
      </div>
    );
  }

  // Conversations list
  return (
    <div className="space-y-4" data-testid="wa-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageSquare size={16} className="text-green-500" />
          <div>
            <h2 className="text-sm font-bold text-[rgb(var(--lm-text))]">WhatsApp</h2>
            <p className="text-[9px] text-[rgb(var(--lm-text-muted))]">{conversations.length} conversation(s) — Reponse IA automatique</p>
          </div>
        </div>
        <div className="flex gap-1.5">
          <button onClick={() => setShowSettings(true)}
            className="p-1.5 rounded-lg border border-[rgb(var(--lm-border))]/30 hover:bg-[rgb(var(--lm-surface))] text-[rgb(var(--lm-text-muted))]"
            title="Reglages IA" data-testid="wa-settings-btn">
            <Settings size={14} />
          </button>
          <button onClick={loadConvos}
            className="p-1.5 rounded-lg border border-[rgb(var(--lm-border))]/30 hover:bg-[rgb(var(--lm-surface))] text-[rgb(var(--lm-text-muted))]"
            title="Rafraichir" data-testid="wa-refresh">
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-[rgb(var(--lm-text-muted))]" /></div>
      ) : conversations.length === 0 ? (
        <div className="text-center py-12">
          <MessageSquare size={32} className="text-green-500/20 mx-auto mb-3" />
          <p className="text-sm text-[rgb(var(--lm-text-muted))]">Aucune conversation WhatsApp</p>
          <p className="text-[10px] text-[rgb(var(--lm-text-muted))] mt-1">Les messages arriveront automatiquement ici</p>
        </div>
      ) : (
        <div className="bg-[rgb(var(--lm-card))] rounded-xl border border-[rgb(var(--lm-border))]/30 divide-y divide-[rgb(var(--lm-border))]/15 overflow-hidden">
          {conversations.map((c, i) => (
            <button key={i} onClick={() => openConvo(c.contact)}
              className="w-full text-left px-4 py-3 hover:bg-[rgb(var(--lm-bg))]/50 transition-colors flex items-center gap-3"
              data-testid={`wa-convo-${i}`}>
              <div className="relative shrink-0">
                <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                  <Phone size={16} className="text-green-500" />
                </div>
                {c.unread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-green-500 rounded-full text-[8px] text-white font-bold flex items-center justify-center">
                    {c.unread}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-baseline gap-2">
                  <span className="text-xs font-bold text-[rgb(var(--lm-text))] truncate">
                    {c.profile_name || formatPhone(c.contact)}
                  </span>
                  <span className="text-[9px] text-[rgb(var(--lm-text-muted))] shrink-0">{formatTime(c.last_date)}</span>
                </div>
                <p className="text-[10px] text-[rgb(var(--lm-text-secondary))] truncate mt-0.5">{c.last_message}</p>
                <span className="text-[9px] text-[rgb(var(--lm-text-muted))]">{c.count} messages</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
